import React, { useCallback, useEffect, useRef, useState } from "react";
import Layout from "../../../../Login/components/layout";
import { Container, Row, Col } from "reactstrap";
import { useIframeSectionSelection } from "../../../../hooks/useIframeSectionSelection";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import SectionEditor from "./sectionEditor";
import { Button } from "@mui/material";
import { connect } from "react-redux";
import { firebaseConnect, isLoaded } from "react-redux-firebase";
import get from "lodash.get";
import { serviceCaller, ServiceCallerProps } from "../../../composition";
import ProtectedRoute from "../../../../Auth/components/ProtectedRoute";
import { ScaleLoader } from "react-spinners";
import {
  usePreviewLoadTimeouts,
  usePreviewOriginFallback,
} from "../../../../hooks/usePreviewOriginFallback";
import PreviewUnavailablePanel from "../shared/PreviewUnavailablePanel";

interface Props extends ServiceCallerProps {
  whitelabelUrl?: string | null;
  customizationIframeOrigin?: string | null;
  fallbackConfigLoaded?: boolean;
}

const CustomizeGivingSiteContent: React.FC<Props> = ({
  whitelabelUrl,
  customizationIframeOrigin,
  fallbackConfigLoaded = false,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const desktopPreviewViewportRef = useRef<HTMLDivElement>(null);
  const [desktopPreviewScale, setDesktopPreviewScale] = useState(1);
  const {
    activeOrigin: iframeOrigin,
    loadState: previewLoadState,
    primaryOrigin,
    handlePreviewReady,
    handlePreviewFailure,
  } = usePreviewOriginFallback(
    whitelabelUrl,
    customizationIframeOrigin,
    fallbackConfigLoaded
  );

  const iframeSrc = iframeOrigin ? `${iframeOrigin}/preview?preview=true` : "";
  const [isMobileView, setIsMobileView] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [previewInitialized, setPreviewInitialized] = useState(false);

  usePreviewLoadTimeouts(
    iframeOrigin,
    iframeLoaded,
    previewInitialized,
    handlePreviewFailure
  );

  const [selectedSection, _] = useIframeSectionSelection(
    iframeRef,
    iframeOrigin || ""
  );

  const sendMessage = useCallback(
    (type: string, payload: Record<string, unknown>) => {
      const win = iframeRef.current?.contentWindow;
      if (win && iframeOrigin) {
        win.postMessage({ type, ...payload }, iframeOrigin);
      }
    },
    [iframeOrigin]
  );

  const handleGoToRoute = useCallback(
    (route: string) => {
      sendMessage("navigate", { url: `${iframeOrigin}/${route}?preview=true` });
    },
    [sendMessage, iframeOrigin]
  );

  const handleSwitchView = useCallback(() => {
    sendMessage("switchView", {
      view: isMobileView ? "web" : "mobile",
    });
    setIsMobileView(prev => !prev);
  }, [isMobileView, sendMessage]);

  useEffect(() => {
    setIframeLoaded(false);
    setPreviewInitialized(false);
  }, [iframeSrc]);

  useEffect(() => {
    if (!iframeLoaded || !iframeOrigin || previewInitialized) {
      return;
    }

    sendMessage("initialize", { values: {} });

    function onMessage(e: MessageEvent) {
      if (e.origin !== iframeOrigin) {
        return;
      }
      if (e.data?.type === "initializationData") {
        setPreviewInitialized(true);
        handlePreviewReady();
        window.removeEventListener("message", onMessage);
      }
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [
    iframeLoaded,
    iframeOrigin,
    previewInitialized,
    sendMessage,
    handlePreviewReady,
  ]);

  useEffect(() => {
    if (isMobileView) {
      setDesktopPreviewScale(1);
      return undefined;
    }

    const viewport = desktopPreviewViewportRef.current;
    if (!viewport) {
      return undefined;
    }

    const updateScale = () => {
      const widthScale = viewport.clientWidth / 1280;
      const heightScale = viewport.clientHeight / 800;
      setDesktopPreviewScale(Math.min(widthScale, heightScale, 1));
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [isMobileView]);

  const previewReady = previewLoadState === "ready";
  const previewFailed = previewLoadState === "failed";
  const previewLoading = previewLoadState === "loading";

  const renderPreviewIframe = () => {
    if (isMobileView) {
      return (
        <iframe
          key={iframeSrc}
          ref={iframeRef}
          src={iframeSrc}
          className={`${s.iframeStyle} ${s.phoneIframe}`}
          onLoad={() => setIframeLoaded(true)}
        />
      );
    }

    return (
      <div
        className={s.desktopPreviewShell}
        style={{ transform: `scale(${desktopPreviewScale})` }}
      >
        <iframe
          key={iframeSrc}
          ref={iframeRef}
          src={iframeSrc}
          className={`${s.iframeStyle} ${s.desktopIframe}`}
          onLoad={() => setIframeLoaded(true)}
        />
      </div>
    );
  };

  return (
    <Layout>
      {previewLoading && (
        <div className={s.spinnerContainer}>
          <ScaleLoader color="#1F65B1" />
        </div>
      )}
      <Container
        fluid
        className={`${s.customizeContainer}`}
        style={previewLoading ? { display: "none" } : undefined}
      >
        <Row className={s.editSectionContainer}>
          <Col md={3} className={`${s.leftSection} px-0`}>
            {previewReady && (
              <div className={`${s.editSectionForms} mt-3 me-3`}>
                <Button
                  color="primary"
                  onClick={() => {
                    handleGoToRoute("preview");
                  }}
                  className="mb-3"
                >
                  Go to Newsfeed
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    handleGoToRoute("donate");
                  }}
                  className="mb-3"
                >
                  Go to Donate
                </Button>
                <Button
                  color="secondary"
                  onClick={() => handleSwitchView()}
                  className="mb-3"
                >
                  {isMobileView ? "Switch to Web View" : "Switch to Phone View"}
                </Button>
                <SectionEditor
                  iframeRef={iframeRef}
                  iframeOrigin={iframeOrigin}
                  selectedSection={selectedSection}
                />
              </div>
            )}
          </Col>
          <Col md={9} className={s.rightSection}>
            {previewReady && (
              <p className={s.editSectionTitle}> Select a section to edit</p>
            )}
            <div
              ref={desktopPreviewViewportRef}
              className={
                isMobileView
                  ? `${s.iframeContainer} ${s.phoneViewport}`
                  : `${s.iframeContainer} ${s.desktopPreviewViewport}`
              }
            >
              {previewFailed && (
                <PreviewUnavailablePanel attemptedUrl={primaryOrigin} />
              )}
              {iframeOrigin && !previewFailed && renderPreviewIframe()}
            </div>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

const CustomizeGivingSite: React.FC<Props> = (props) => (
  <ProtectedRoute>
    <CustomizeGivingSiteContent {...props} />
  </ProtectedRoute>
);

const mapStateToProps = state => {
  const { firebase, user } = state;
  const charityId = user.currentCharity.id;
  const whitelabelUrl = get(
    firebase,
    `data.${charityId}.config.whitelabelUrl`,
    null
  );
  const fallbackConfigLoaded = isLoaded(get(firebase, "data.global.config"));
  const customizationIframeOrigin = fallbackConfigLoaded
    ? get(firebase, `data.global.config.customizationIframeOrigin`, null)
    : undefined;
  return {
    whitelabelUrl,
    customizationIframeOrigin,
    fallbackConfigLoaded,
  };
};

const CustomizeGivingSiteEnhanced = serviceCaller(
  connect(mapStateToProps)(
    firebaseConnect((_, store) => {
      const charityId = store.getState().user.currentCharity.id;
      return charityId !== ""
        ? [
            `${charityId}/config/whitelabelUrl`,
            `global/config`,
            `global/config/customizationIframeOrigin`,
          ]
        : [];
    })(withStyles(s)(CustomizeGivingSite))
  )
);
export default CustomizeGivingSiteEnhanced;
